import { useState } from "react";
import axios from "axios";
import { CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VOLUMES = ["Under 500", "500–2,000", "2,000–5,000", "5,000+"];

export default function DemoForm() {
  const [form, setForm] = useState({
    full_name: "",
    company_name: "",
    email: "",
    phone: "",
    market: "",
    ticket_volume: "",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const set = (k) => (e) => {
    setForm((s) => ({ ...s, [k]: e.target.value }));
    if (errors[k]) setErrors((s) => ({ ...s, [k]: undefined }));
  };

  const validate = () => {
    const err = {};
    if (!form.full_name.trim()) err.full_name = "Required";
    if (!form.company_name.trim()) err.company_name = "Required";
    if (!form.email.trim()) err.email = "Required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) err.email = "Invalid email";
    if (!form.phone.trim()) err.phone = "Required";
    if (!form.market.trim()) err.market = "Required";
    if (!form.ticket_volume) err.ticket_volume = "Required";
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error("Please complete required fields.");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/demo-requests`, form);
      setSuccess(true);
      toast.success("Request received — we'll be in touch within 1 business day.");
    } catch (err) {
      console.error(err);
      toast.error("Couldn't submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div
        data-testid="demo-success"
        className="reveal in-view rounded-2xl glass-strong p-10 md:p-14 text-center max-w-2xl mx-auto"
      >
        <div className="mx-auto h-16 w-16 rounded-full bg-[#22C55E]/15 border border-[#22C55E]/40 grid place-items-center">
          <CheckCircle2 size={28} className="text-[#22C55E]" />
        </div>
        <h3 className="mt-5 font-display font-bold text-2xl md:text-3xl text-white">
          Request received.
        </h3>
        <p className="mt-3 text-[#94A3B8] leading-relaxed">
          A LocateOS team member will reach out within 1 business day.
        </p>
      </div>
    );
  }

  const inputCls = (k) =>
    `w-full bg-[#0a0a0a] border ${
      errors[k] ? "border-[#EF4444]/60" : "border-white/10"
    } rounded-lg px-4 py-3 text-sm text-white placeholder:text-[#94A3B8] focus:outline-none focus:border-[#F97316] transition-colors`;

  return (
    <form
      data-testid="demo-form"
      onSubmit={onSubmit}
      className="rounded-2xl glass p-6 md:p-8 max-w-3xl mx-auto"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="text-xs font-medium text-[#94A3B8] uppercase tracking-wider">Full Name *</label>
          <input data-testid="demo-input-name" className={`mt-2 ${inputCls("full_name")}`} value={form.full_name} onChange={set("full_name")} placeholder="Jane Doe" />
          {errors.full_name && <div className="text-xs text-[#EF4444] mt-1">{errors.full_name}</div>}
        </div>
        <div>
          <label className="text-xs font-medium text-[#94A3B8] uppercase tracking-wider">Company *</label>
          <input data-testid="demo-input-company" className={`mt-2 ${inputCls("company_name")}`} value={form.company_name} onChange={set("company_name")} placeholder="Acme Locating Co." />
          {errors.company_name && <div className="text-xs text-[#EF4444] mt-1">{errors.company_name}</div>}
        </div>
        <div>
          <label className="text-xs font-medium text-[#94A3B8] uppercase tracking-wider">Email *</label>
          <input data-testid="demo-input-email" type="email" className={`mt-2 ${inputCls("email")}`} value={form.email} onChange={set("email")} placeholder="jane@company.com" />
          {errors.email && <div className="text-xs text-[#EF4444] mt-1">{errors.email}</div>}
        </div>
        <div>
          <label className="text-xs font-medium text-[#94A3B8] uppercase tracking-wider">Phone *</label>
          <input data-testid="demo-input-phone" className={`mt-2 ${inputCls("phone")}`} value={form.phone} onChange={set("phone")} placeholder="(555) 123-4567" />
          {errors.phone && <div className="text-xs text-[#EF4444] mt-1">{errors.phone}</div>}
        </div>
        <div>
          <label className="text-xs font-medium text-[#94A3B8] uppercase tracking-wider">State / Market *</label>
          <input data-testid="demo-input-market" className={`mt-2 ${inputCls("market")}`} value={form.market} onChange={set("market")} placeholder="Georgia, Tennessee…" />
          {errors.market && <div className="text-xs text-[#EF4444] mt-1">{errors.market}</div>}
        </div>
        <div>
          <label className="text-xs font-medium text-[#94A3B8] uppercase tracking-wider">Monthly Ticket Volume *</label>
          <select data-testid="demo-input-volume" className={`mt-2 ${inputCls("ticket_volume")}`} value={form.ticket_volume} onChange={set("ticket_volume")}>
            <option value="">Select range…</option>
            {VOLUMES.map((v) => (
              <option key={v} value={v}>{v}</option>
            ))}
          </select>
          {errors.ticket_volume && <div className="text-xs text-[#EF4444] mt-1">{errors.ticket_volume}</div>}
        </div>
        <div className="md:col-span-2">
          <label className="text-xs font-medium text-[#94A3B8] uppercase tracking-wider">Message</label>
          <textarea
            data-testid="demo-input-message"
            rows={4}
            className={`mt-2 ${inputCls("message")} resize-y`}
            value={form.message}
            onChange={set("message")}
            placeholder="Tell us about your operation, current tooling, and what you'd like to see."
          />
        </div>
      </div>

      <button
        data-testid="demo-submit-btn"
        type="submit"
        disabled={loading}
        className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#F97316] hover:bg-[#ea580c] disabled:opacity-60 text-white font-semibold py-3.5 rounded-lg transition-all shadow-[0_8px_32px_rgba(249,115,22,0.35)]"
      >
        {loading ? (
          <>
            <Loader2 size={16} className="animate-spin" /> Submitting…
          </>
        ) : (
          "Request Demo"
        )}
      </button>

      <p className="mt-4 text-[11px] text-[#94A3B8] text-center">
        By submitting you agree to receive operational follow-ups from LocateOS. We respect your inbox.
      </p>
    </form>
  );
}
